import { apiClient } from "./client";
import type { ComponentDetail } from "./types";

export interface CompanyAssessmentSummary {
  id: string;
  decision:
    | "QUALIFIED"
    | "NOT_QUALIFIED"
    | "NEEDS_DATA"
    | "NEEDS_REVIEW"
    | "SUPPRESSED";
  score: number | null;
  provisional: boolean;
  current: boolean;
  evaluated_at: string;
  valid_until: string | null;
  complete_dimensions: number;
  total_dimensions: number;
  missing_dimensions: string[];
  quality_states: string[];
  next_dimension: string | null;
  config_fingerprint: string;
  outreach_eligible: boolean;
}
export interface CompanyDataDimension {
  dimension: string;
  status:
    | "COMPLETE"
    | "PARTIAL"
    | "MISSING"
    | "STALE"
    | "CONTRADICTED"
    | "SOURCE_UNAVAILABLE";
  reason: string;
  observed_at: string | null;
  valid_until: string | null;
  next_action: string | null;
  sources: {
    url?: string;
    source?: string;
    observed_at?: string;
    sha256?: string;
  }[];
  investigations: {
    source: string;
    status: string;
    reference: string;
    detail: string;
    observed_at: string;
  }[];
}
export interface CompanyAssessmentDetail extends CompanyAssessmentSummary {
  funding: {
    stage: string;
    announced_on: string;
    amount_usd: number | null;
    currency: string | null;
    amount_basis: string;
  } | null;
  reasons: string[];
  dimensions: CompanyDataDimension[];
  components: ComponentDetail[];
  score_lower: number | null;
  score_upper: number | null;
  threshold: number | null;
  engine_version: string;
  cohort_notice: string;
  can_review: boolean;
}
export interface CompanyEvidenceReview {
  expected_assessment_id: string;
  dimension:
    | "finance_team_level"
    | "has_hr_lead"
    | "has_cos_coo"
    | "hiring_finance_level"
    | "hiring_stratops"
    | "hiring_hr"
    | "is_excluded_incubator"
    | "is_blacklisted_sector";
  value: string;
  source_url: string;
  observed_at: string;
  coverage: "complete" | "partial";
  reason: string;
}

// Mirrored from the Growth* schemas in docs/openapi.json.
export type GrowthView =
  | "pending"
  | "partial"
  | "complete"
  | "waiting"
  | "blocked"
  | "all"
  | "data_pending"
  | "scored"
  | "qualified"
  | "not_qualified";
export type GrowthFilter =
  | "email_missing"
  | "email_risky"
  | "identity"
  | "affiliation"
  | "attribution"
  | "eligibility"
  | "suppression"
  | "stale"
  | "high_priority"
  | "data_missing"
  | "data_stale"
  | "data_conflict"
  | "founder_incomplete";
export type GrowthPriority =
  | "QUICK_WIN"
  | "RESEARCH_REQUIRED"
  | "PAID_VERIFICATION_POSSIBLE"
  | "HUMAN_REVIEW"
  | "BLOCKED";
export interface GrowthCompany {
  assessment: CompanyAssessmentSummary | null;
  company_id: number;
  canonical_company_uuid: string;
  source_company_uuids: string[];
  name: string;
  domain: string | null;
  funding_stage: string;
  eligibility: string;
  historical_uuid_count: number;
  founders: number;
  verified: number;
  remaining: number;
  excluded_founders: number;
  excluded_aliases: number;
  emails: number;
  deliverable: number;
  reusable_deliverable: number;
  accept_all: number;
  risky: number;
  unchecked_emails: number;
  missing_emails: number;
  unattributed_emails: number;
  commercial_pending: number;
  blockers: string[];
  last_checked_at: string | null;
  priority: GrowthPriority;
  priority_score: number;
  priority_reasons: string[];
  next_action: string;
  view: string;
  filters: string[];
}
export interface GrowthFounder {
  id: string;
  revision: number;
  canonical_person_uuid: string;
  name: string;
  source_uuids: {
    uuid?: string;
    qualification?: string;
    alias?: boolean;
    exclusion_reason?: string;
  }[];
  linkedin: string | null;
  identity_status: string;
  affiliation_status: string;
  historical_role: string;
  current_role: string;
  current_company: string;
  target_company: string;
  email: string | null;
  alternative_emails: Record<string, unknown>[];
  email_company: string;
  email_attribution_status: string;
  email_attributed: boolean;
  bouncer_status: string;
  bouncer_raw_status: string;
  bouncer_checked_at: string | null;
  bouncer_fresh: boolean;
  email_sources: unknown[];
  historical_qualification: string;
  historical_verified_on_merits: boolean;
  verified_on_merits: boolean;
  eligibility: string;
  excluded: boolean;
  suppression_status: string;
  commercial_status: string;
  campaign_eligible: boolean;
  outreach_history: Record<string, unknown>;
  campaign_conflict: string;
  blockers: string[];
  missing_proofs: string[];
  contradictions: string[];
  evidence: Record<string, unknown>[];
  source_references: Record<string, string>[];
  last_checked_at: string | null;
  priority: GrowthPriority;
  priority_reasons: string[];
  next_action: string;
  filters: string[];
  reviews: Record<string, unknown>[];
  prepared_verification: Record<string, unknown> | null;
  hidden_details: string[];
}
export interface GrowthSummary {
  assessment_companies: number | null;
  data_complete_companies: number | null;
  data_pending_companies: number | null;
  decisions: Record<string, number>;
  companies: number;
  pending_companies: number;
  partial_companies: number;
  complete_companies: number;
  blocked_companies: number;
  founders: number;
  historical_uuids: number;
  historical_verified: number;
  historical_unverified_uuids: number;
  remaining_founders: number;
  queue_founders: number;
  excluded_founders: number;
  excluded_aliases: number;
  priorities: Record<string, number>;
}
export interface GrowthQueue {
  items: GrowthCompany[];
  total: number;
  page: number;
  page_size: number;
  summary: GrowthSummary;
  calculated_at: string;
  can_review: boolean;
  can_view_blocked: boolean;
}
export interface GrowthScoreRule {
  key: string;
  label: string;
  points: number | null;
  max_points: number;
  status: "DOCUMENTED" | "MISSING_DATA" | "DISABLED";
  evidence: string | null;
  source: string | null;
}
export interface GrowthScore {
  points: number | null;
  max_points: number;
  known_rules: number;
  total_rules: number;
  status: "PARTIAL" | "DOCUMENTED" | "BLOCKED" | "UNAVAILABLE";
  rules: GrowthScoreRule[];
  blockers: string[];
  calculated_at: string;
  config_fingerprint: string;
}
export interface GrowthDetail {
  assessment: CompanyAssessmentDetail | null;
  score: GrowthScore | null;
  company: GrowthCompany;
  founders: GrowthFounder[];
  company_evidence: Record<string, unknown>;
  can_review: boolean;
  provider_dispatch_enabled: boolean;
  provider_notice: string;
}
export type GrowthReviewField =
  | "identity_status"
  | "affiliation_status"
  | "current_role"
  | "current_company"
  | "email_attribution_status"
  | "email"
  | "eligibility"
  | "note";
export interface GrowthReview {
  request_id: string;
  expected_revision: number;
  field: GrowthReviewField;
  value: string;
  source_url: string;
  evidence_at: string;
  reason: string;
}
const base = (slug: string) =>
  `/projects/${encodeURIComponent(slug)}/enrichment/us-growth`;
export async function getGrowthQueue(
  slug: string,
  options: {
    view: GrowthView;
    filter: GrowthFilter | "";
    search: string;
    sort: string;
    page: number;
  },
  signal?: AbortSignal,
): Promise<GrowthQueue> {
  const params = new URLSearchParams({
    view: options.view,
    search: options.search,
    sort: options.sort,
    page: String(options.page),
    page_size: "25",
  });
  if (options.filter) params.append("filters", options.filter);
  return (await apiClient.get<GrowthQueue>(base(slug), { params, signal }))
    .data;
}
export async function getGrowthDetail(
  slug: string,
  id: number,
  signal?: AbortSignal,
): Promise<GrowthDetail> {
  return (
    await apiClient.get<GrowthDetail>(`${base(slug)}/companies/${id}`, {
      signal,
    })
  ).data;
}
export async function reviewGrowth(
  slug: string,
  company: number,
  founder: string,
  body: GrowthReview,
): Promise<GrowthDetail> {
  return (
    await apiClient.post<GrowthDetail>(
      `${base(slug)}/companies/${company}/founders/${founder}/review`,
      body,
    )
  ).data;
}
export async function prepareGrowthBouncer(
  slug: string,
  company: number,
  founder: GrowthFounder,
  requestId: string,
): Promise<GrowthDetail> {
  return (
    await apiClient.post<GrowthDetail>(
      `${base(slug)}/companies/${company}/founders/${founder.id}/prepare-bouncer`,
      { request_id: requestId, expected_revision: founder.revision },
    )
  ).data;
}

export async function saveCompanyEvidence(
  slug: string,
  companyId: number,
  body: CompanyEvidenceReview,
): Promise<GrowthDetail> {
  const { data } = await apiClient.post<GrowthDetail>(
    `${base(slug)}/companies/${companyId}/assessment/evidence`,
    body,
  );
  return data;
}
export async function recalculateCompanyAssessment(
  slug: string,
  companyId: number,
  expectedAssessmentId: string,
): Promise<GrowthDetail> {
  const { data } = await apiClient.post<GrowthDetail>(
    `${base(slug)}/companies/${companyId}/assessment/recalculate`,
    { expected_assessment_id: expectedAssessmentId },
  );
  return data;
}
